
import { Component, Prop, Watch } from 'vue-property-decorator';
import VueApp from '@/@types/app/VueApp';
import Search from '../inputs/GlobalSearch.vue';
import EventBus from '@/EventBus';
import UserMenu from './UserMenu.vue';
import Logo from '../shared/Logo.vue';
import { ARTICLE_CREATE, TRIGGER_SEARCH_INPUT } from '@/events';
import { BILLING_PLANS, CHECKOUT, HOME, INACTIVE_COMPANY } from '@/routerNames';
import ExtensionBanner from '@/components/layout/header/ExtensionBanner.vue';
import { User } from '@/models/User';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import { Mutations } from '@/store/mutations';
import { commitToggleMainSidebar } from '@/store/commits/uiCommits';
import NotificationButton from '@/components/layout/header/NotificationButton/NotificationButton.vue';
import { getProductsUrl } from '@/helpers/productsUrl';
import notificationService from '@/services/NotificationService';
import { SNAPLORE_APP, hasGuidesAccess, isSnaploreClosedFor } from '@/helpers/appContext';

const EXTENSION_BANNER_DELAY = 5000;

let handleFocusOut: any;
@Component({
  components: {
    NotificationButton,
    UserMenu,
    Search,
    Logo,
    ExtensionBanner,
  },
  directives: {
    'focus-out': {
      bind: (el: any, binding: any, vnode: any) => {
        handleFocusOut = (event: any) => {
          const isClickedInside = event.target.classList.contains('userMenu');
          const isMobileMenuOpen = vnode.context.isMenuOpened;
          const isButtonTriggers = event.target.classList.contains('user-menu-trigger');

          if (isButtonTriggers) {
            return;
          }

          if (isMobileMenuOpen && !isClickedInside) {
            vnode.context.toggleUserMenu();
          }
        };
        if (vnode.context.isMobile) {
          document.body.addEventListener('click', handleFocusOut);
        }
      },
      unbind: () => {
        document.body.removeEventListener('click', handleFocusOut);
      },
    },
  },
})
export default class Header extends VueApp {
  @Prop({ type: Boolean, default: false }) isSidebar: boolean;

  hideLinks = false;
  isMenuOpened = false;
  windowWidth: number;

  get user(): User {
    return this.$store.state.user;
  }

  get isUserInactiveInCompany() {
    return this.$store.state.isUserInactiveInCompany;
  }

  get company() {
    if (this.$store.state.pageCompany) {
      return this.$store.state.pageCompany;
    } else if (this.$store.state.company) {
      return this.$store.state.company;
    }
    return null;
  }

  get isCompanyLoading() {
    return this.$store.state.isCompanyLoading;
  }

  get isBillingPage() {
    return this.$route.name === CHECKOUT || this.$route.name === BILLING_PLANS;
  }

  get isCompanyPaid() {
    return this.company?.isPaid;
  }

  // SNAP-2241: a company that pays only for Guides gets no search, create or notifications here (they belong to
  // Snaplore, and the notifications panel lives in the sidebar it does not have).
  get showSnaploreControls() {
    return this.isCompanyPaid && !isSnaploreClosedFor(this.company, this.user);
  }

  get isHome() {
    return this.$route.name === HOME;
  }

  get isGuest() {
    return this.$auth.isGuest();
  }

  get isCompanyDeactivated(): boolean {
    return !this.$store.state.company.isActivated;
  }

  get isMobile(): boolean {
    return mobileResponsiveService.isMobileView;
  }

  get isExtensionEnabled(): boolean {
    return this.$store.state.extension.isEnabled;
  }

  /**
   * URL of the snaplore-products portal (How-tos and future products).
   *
   * In prod + stage the products app lives on its own dedicated subdomain
   * (`products.<zone>`) — NOT path-mounted on the tenant subdomain — so we
   * dodge the Cloudflare Worker / shared-zone-wildcard mess that breaks
   * other apps hosted on the same zone. Cookie scope `.snaplore.com` /
   * `.testinghub.xyz` keeps auth shared across the legacy and products
   * subdomains.
   *
   * Override via `VUE_APP_PRODUCTS_URL` in `.env.local` if your products
   * dev server runs on a different port.
   */
  get productsUrl(): string {
    return getProductsUrl();
  }

  // `isProductsExternal` removed along with the standalone How-tos
  // button — the cross-app jump now goes through `handleProductPick`,
  // which checks the URL itself for http(s) before deciding tab target.

  /**
   * Top-left product selector items — mirrors the snaplore-products portal's
   * ProductSelectorPage so users get the same four-item menu in either app.
   *
   * - SNAPLORE: this app, always listed; clicking just stays on the current tenant root (acts as a
   *   "home" jump from deep pages). A company paying only for Guides goes to the Snaplore plans (SNAP-2241).
   * - How-tos: live; opens the products portal at /howto.
   * - Knowledge base: forced "Soon" — reserved for a future dedicated KB
   *   product, not the current legacy app (which fills that role today).
   * - Meetings: forced "Soon" — product not built yet.
   */
  get productMenuEntries() {
    return [
      { id: 'snaplore', name: 'SNAPLORE', description: 'Articles, notes, search.', live: true, url: '/' },
      {
        id: 'howto',
        name: 'Guides',
        description: 'Step-by-step guides from recordings.',
        live: true,
        url: this.productsUrl,
      },
      { id: 'kb', name: 'Knowledge base', description: 'Dedicated KB product.', live: false, url: '' },
      { id: 'meetings', name: 'Meetings', description: 'Auto-summarised calls.', live: false, url: '' },
    ];
  }

  handleProductPick(entry: { id: string; live: boolean; url: string }) {
    if (!entry.live || !entry.url) return;
    if (entry.id === 'howto' && !hasGuidesAccess(this.$store.state.company)) {
      // A company without a paid Guides entitlement goes to the Guides plans
      // page instead of straight into the Guides app (SNAP-2241 D1/D2). Only the owner can pay.
      if (this.user?.role === 'owner' || this.user?.isSuperAdmin()) {
        this.$router.push({ name: BILLING_PLANS, query: { app: 'guides' } });
      } else {
        notificationService.warning('Guides is not on your company plan. Ask your company owner to add it.');
      }
      return;
    }
    if (entry.id === 'snaplore' && isSnaploreClosedFor(this.company, this.user)) {
      // Roman, 2026-11-04: "if company snaplore not paid show billing". Only the owner can pay.
      if (this.user?.role === 'owner') {
        this.$router.push({ name: BILLING_PLANS, query: { app: SNAPLORE_APP } });
      } else {
        notificationService.warning('Snaplore is not on your company plan. Ask your company owner to add it.');
      }
      return;
    }
    if (entry.id === 'snaplore') {
      // Same-origin nav — use the router so we don't full-reload when the
      // user is already inside the SPA.
      if (this.$route?.path !== '/') this.$router.push('/');
      return;
    }
    // Cross-app jumps (products portal etc.) — full navigation.
    const opensExternal = entry.url.startsWith('http://') || entry.url.startsWith('https://');
    if (opensExternal && process.env.NODE_ENV !== 'production') {
      window.open(entry.url, '_blank');
    } else {
      window.location.assign(entry.url);
    }
  }

  async checkCompanyActivatedStatus() {
    try {
      const company = await this.$api.company.getCompanyStatus();
      if (company && !company.data.isActivated) {
        this.$store.commit(Mutations.SET_COMPANY_STATUS, company.data.isActivated);
        return false;
      }
      return true;
    } catch (err: any) {
      if (err.response.data && err.response.data.company) {
        this.$store.commit(Mutations.SET_COMPANY_STATUS, err.response.data.company.isActivated);
      }
      this.$api.handleResponseError(err);
      return false;
    }
  }

  async handleCreateArticle() {
    const companyDeactivated = !(await this.checkCompanyActivatedStatus());
    if (companyDeactivated) {
      return this.$router.push({ name: INACTIVE_COMPANY });
    }
    EventBus.$emit(ARTICLE_CREATE);
  }

  async handleAddWorkspace() {
    const companyDeactivated = !(await this.checkCompanyActivatedStatus());
    if (companyDeactivated) {
      return this.$router.push({ name: INACTIVE_COMPANY });
    }
    this.$store.commit('setIsWorkspaceCreateDialogVisible', true);
  }

  async updateExtensionBanner() {
    this.$store.commit('setIsExtensionBannerVisible', !this.$store.state.extension.id && this.user);
  }

  //responsive handlers
  toggleUserMenu() {
    this.isMenuOpened = !this.isMenuOpened;
  }

  handleWindowResize() {
    this.windowWidth = window.innerWidth;
  }

  toggleMobileSidebar() {
    commitToggleMainSidebar();
  }

  created() {
    setTimeout(() => this.updateExtensionBanner(), EXTENSION_BANNER_DELAY);
    window.addEventListener('resize', this.handleWindowResize);
    this.handleWindowResize();
  }

  mounted() {
    EventBus.$on(TRIGGER_SEARCH_INPUT, (trigger: boolean) => {
      this.hideLinks = trigger;
    });
  }

  beforeDestroy() {
    EventBus.$off(TRIGGER_SEARCH_INPUT);
  }

  @Watch('isExtensionEnabled')
  onExtensionStatusChanged(value: boolean) {
    if (value) {
      this.updateExtensionBanner();
    }
  }
}
